import * as Astronomy from "astronomy-engine";
import { birthDataToUtcDate } from "./skyProjection";

// ─── Reference data ───────────────────────────────────────────────────────────

const VEDIC_SIGNS = [
  { name: "Aries",       sanskrit: "Mesha" },
  { name: "Taurus",      sanskrit: "Vrishabha" },
  { name: "Gemini",      sanskrit: "Mithuna" },
  { name: "Cancer",      sanskrit: "Karka" },
  { name: "Leo",         sanskrit: "Simha" },
  { name: "Virgo",       sanskrit: "Kanya" },
  { name: "Libra",       sanskrit: "Tula" },
  { name: "Scorpio",     sanskrit: "Vrishchika" },
  { name: "Sagittarius", sanskrit: "Dhanus" },
  { name: "Capricorn",   sanskrit: "Makara" },
  { name: "Aquarius",    sanskrit: "Kumbha" },
  { name: "Pisces",      sanskrit: "Meena" },
];

// 27 nakshatras in order. Each is 13°20' (800') wide.
// Ruling planet sequence: Ke Ve Su Mo Ma Ra Ju Sa Me (repeating)
const NAKSHATRA_RULERS = ["ketu","venus","sun","moon","mars","rahu","jupiter","saturn","mercury"];
const NAKSHATRAS = [
  { name: "Ashwini",          sanskrit: "अश्विनी",       ruler: NAKSHATRA_RULERS[0] },
  { name: "Bharani",          sanskrit: "भरणी",           ruler: NAKSHATRA_RULERS[1] },
  { name: "Krittika",         sanskrit: "कृत्तिका",       ruler: NAKSHATRA_RULERS[2] },
  { name: "Rohini",           sanskrit: "रोहिणी",         ruler: NAKSHATRA_RULERS[3] },
  { name: "Mrigashira",       sanskrit: "मृगशिरा",       ruler: NAKSHATRA_RULERS[4] },
  { name: "Ardra",            sanskrit: "आर्द्रा",         ruler: NAKSHATRA_RULERS[5] },
  { name: "Punarvasu",        sanskrit: "पुनर्वसु",       ruler: NAKSHATRA_RULERS[6] },
  { name: "Pushya",           sanskrit: "पुष्य",           ruler: NAKSHATRA_RULERS[7] },
  { name: "Ashlesha",         sanskrit: "अश्लेषा",       ruler: NAKSHATRA_RULERS[8] },
  { name: "Magha",            sanskrit: "मघा",             ruler: NAKSHATRA_RULERS[0] },
  { name: "Purva Phalguni",   sanskrit: "पूर्व फाल्गुनी", ruler: NAKSHATRA_RULERS[1] },
  { name: "Uttara Phalguni",  sanskrit: "उत्तर फाल्गुनी", ruler: NAKSHATRA_RULERS[2] },
  { name: "Hasta",            sanskrit: "हस्त",             ruler: NAKSHATRA_RULERS[3] },
  { name: "Chitra",           sanskrit: "चित्रा",           ruler: NAKSHATRA_RULERS[4] },
  { name: "Swati",            sanskrit: "स्वाती",           ruler: NAKSHATRA_RULERS[5] },
  { name: "Vishakha",         sanskrit: "विशाखा",         ruler: NAKSHATRA_RULERS[6] },
  { name: "Anuradha",         sanskrit: "अनुराधा",       ruler: NAKSHATRA_RULERS[7] },
  { name: "Jyeshtha",         sanskrit: "ज्येष्ठा",       ruler: NAKSHATRA_RULERS[8] },
  { name: "Mula",             sanskrit: "मूल",              ruler: NAKSHATRA_RULERS[0] },
  { name: "Purva Ashadha",    sanskrit: "पूर्वाषाढ़ा",    ruler: NAKSHATRA_RULERS[1] },
  { name: "Uttara Ashadha",   sanskrit: "उत्तराषाढ़ा",   ruler: NAKSHATRA_RULERS[2] },
  { name: "Shravana",         sanskrit: "श्रवण",           ruler: NAKSHATRA_RULERS[3] },
  { name: "Dhanishta",        sanskrit: "धनिष्ठा",       ruler: NAKSHATRA_RULERS[4] },
  { name: "Shatabhisha",      sanskrit: "शतभिषा",         ruler: NAKSHATRA_RULERS[5] },
  { name: "Purva Bhadrapada", sanskrit: "पूर्व भाद्रपद", ruler: NAKSHATRA_RULERS[6] },
  { name: "Uttara Bhadrapada",sanskrit: "उत्तर भाद्रपद", ruler: NAKSHATRA_RULERS[7] },
  { name: "Revati",           sanskrit: "रेवती",           ruler: NAKSHATRA_RULERS[8] },
];

// Vimshottari Dasha sequence: planet → years in the 120-year cycle
const DASHA_SEQUENCE = [
  { planet: "ketu",    years: 7  },
  { planet: "venus",   years: 20 },
  { planet: "sun",     years: 6  },
  { planet: "moon",    years: 10 },
  { planet: "mars",    years: 7  },
  { planet: "rahu",    years: 18 },
  { planet: "jupiter", years: 16 },
  { planet: "saturn",  years: 19 },
  { planet: "mercury", years: 17 },
];
const DASHA_TOTAL_YEARS = 120;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mod360 = (x) => ((x % 360) + 360) % 360;

// Lahiri ayanamsa: Chandra Hari / standard Lahiri formula.
// Reference epoch: J2000.0. Ayanamsa at J2000 ≈ 23.8556° (23°51'21.4").
// Precession rate: 50.2796" per Julian year = 0.013955° per year.
function calcAyanamsa(jd) {
  const T = (jd - 2451545.0) / 36525.0; // Julian centuries from J2000
  // Standard Lahiri value: 23.85694444° at J2000 + precession
  const ayanamsaJ2000 = 23.85694444;
  const precessionPerCentury = 1.3955; // degrees per Julian century (50.2796" * 36525 / 3600)
  return ayanamsaJ2000 + precessionPerCentury * T;
}

function dateToJD(utcDate) {
  return utcDate.getTime() / 86400000 + 2440587.5;
}

function getNakshatra(siderealDeg) {
  const deg = mod360(siderealDeg);
  const nakshatraWidth = 360 / 27; // 13.3333°
  const padaWidth = nakshatraWidth / 4; // 3.3333°
  const idx = Math.floor(deg / nakshatraWidth);
  const posInNakshatra = deg - idx * nakshatraWidth;
  const pada = Math.floor(posInNakshatra / padaWidth) + 1;
  const nk = NAKSHATRAS[idx];
  return {
    name: nk.name,
    sanskrit: nk.sanskrit,
    pada: Math.min(pada, 4),
    rulingPlanet: nk.ruler,
    // fraction through the nakshatra (0-1), needed for dasha balance
    progressFraction: posInNakshatra / nakshatraWidth,
    index: idx,
  };
}

function makePlacement(siderealDeg, lagnaIdx, hasBirthTime) {
  const deg = mod360(siderealDeg);
  const signIdx = Math.floor(deg / 30);
  const sign = VEDIC_SIGNS[signIdx];
  const degreeInSign = +(deg % 30).toFixed(2);
  const house = hasBirthTime ? ((signIdx - lagnaIdx + 12) % 12) + 1 : null;
  return {
    sign: sign.name,
    signSanskrit: sign.sanskrit,
    signIndex: signIdx,
    degree: degreeInSign,
    house,
  };
}

// ─── Dasha calculation ────────────────────────────────────────────────────────

function addYears(date, years) {
  const ms = years * 365.25 * 24 * 60 * 60 * 1000;
  return new Date(date.getTime() + ms);
}

function formatDate(d) {
  return d.toISOString().slice(0, 10);
}

function calcDasha(moonNakshatra, birthUtcDate, todayDate) {
  // The ruling planet of the Moon's nakshatra starts the dasha sequence.
  // The balance (portion remaining) is (1 - progressFraction) of that first period.
  const birthRuler = moonNakshatra.rulingPlanet;
  const startIdx = DASHA_SEQUENCE.findIndex((d) => d.planet === birthRuler);
  if (startIdx === -1) return null;

  const firstDasha = DASHA_SEQUENCE[startIdx];
  const balanceYears = (1 - moonNakshatra.progressFraction) * firstDasha.years;

  // Build the full sequence of Mahadasha periods starting from birth
  const mahadashas = [];
  let cursor = new Date(birthUtcDate.getTime());

  // First Mahadasha (partial)
  const firstEnd = addYears(cursor, balanceYears);
  // The birth mahadasha began before birth (the elapsed portion), and its
  // antardashas are laid out from that theoretical start.
  const firstStart = addYears(cursor, -(firstDasha.years - balanceYears));
  mahadashas.push({ planet: firstDasha.planet, years: firstDasha.years, start: firstStart, end: firstEnd, balance: balanceYears });
  cursor = firstEnd;

  // Remaining Mahadashas in sequence (full periods)
  for (let i = 1; i < 9; i++) {
    const seq = DASHA_SEQUENCE[(startIdx + i) % 9];
    const end = addYears(cursor, seq.years);
    mahadashas.push({ planet: seq.planet, years: seq.years, start: new Date(cursor), end });
    cursor = end;
  }

  // Find current Mahadasha
  const now = todayDate;
  const currentMahaIdx = mahadashas.findIndex((m) => now >= m.start && now < m.end);
  if (currentMahaIdx === -1) return null;

  const currentMaha = mahadashas[currentMahaIdx];

  // Build Antardashas within the current Mahadasha
  // Antardasha sequence starts from the Mahadasha lord and follows the same order.
  const mahaStartIdx = DASHA_SEQUENCE.findIndex((d) => d.planet === currentMaha.planet);
  const antardashas = [];
  let antarCursor = new Date(currentMaha.start.getTime());
  for (let i = 0; i < 9; i++) {
    const seq = DASHA_SEQUENCE[(mahaStartIdx + i) % 9];
    const antarYears = (seq.years / DASHA_TOTAL_YEARS) * currentMaha.years;
    const antarEnd = addYears(antarCursor, antarYears);
    antardashas.push({ planet: seq.planet, start: new Date(antarCursor), end: antarEnd });
    antarCursor = antarEnd;
  }

  const currentAntarIdx = antardashas.findIndex((a) => now >= a.start && now < a.end);
  if (currentAntarIdx === -1) return null;

  const currentAntar = antardashas[currentAntarIdx];
  const nextAntar = antardashas[currentAntarIdx + 1] || null;

  return {
    current: {
      maha: {
        planet: currentMaha.planet,
        startDate: formatDate(currentMaha.start),
        endDate: formatDate(currentMaha.end),
        years: currentMaha.years,
      },
      antar: {
        planet: currentAntar.planet,
        startDate: formatDate(currentAntar.start),
        endDate: formatDate(currentAntar.end),
      },
    },
    next: nextAntar
      ? { antar: { planet: nextAntar.planet, startDate: formatDate(nextAntar.start), endDate: formatDate(nextAntar.end) } }
      : null,
  };
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function calculateVedicChart(birthData) {
  const { hasBirthTime, birthLocation } = birthData;

  // True UTC of birth (DST-aware). Shared with StarSky via skyProjection.
  const utcDate = birthDataToUtcDate(birthData);

  const astroTime = Astronomy.MakeTime(utcDate);
  const jd = dateToJD(utcDate);
  const ayanamsa = calcAyanamsa(jd);

  // Tropical longitudes for the seven classical + Rahu/Ketu
  const bodies = {
    sun:     Astronomy.Body.Sun,
    moon:    Astronomy.Body.Moon,
    mercury: Astronomy.Body.Mercury,
    venus:   Astronomy.Body.Venus,
    mars:    Astronomy.Body.Mars,
    jupiter: Astronomy.Body.Jupiter,
    saturn:  Astronomy.Body.Saturn,
  };

  const tropicalLong = {};
  for (const [key, body] of Object.entries(bodies)) {
    const vec = Astronomy.GeoVector(body, astroTime, true);
    const ecl = Astronomy.Ecliptic(vec);
    tropicalLong[key] = mod360(ecl.elon);
  }

  // Mean lunar node (Rahu = North Node). Same formula as calculateLocalChart.
  const daysSinceJ2000 = utcDate.getTime() / 86400000 - 10957.5;
  const T = daysSinceJ2000 / 36525;
  tropicalLong.rahu = mod360(125.04 - 1934.136 * T);
  tropicalLong.ketu = mod360(tropicalLong.rahu + 180);

  // Convert all to sidereal by subtracting Lahiri ayanamsa
  const siderealLong = {};
  for (const key of Object.keys(tropicalLong)) {
    siderealLong[key] = mod360(tropicalLong[key] - ayanamsa);
  }

  // Lagna (Ascendant) — tropical, then convert to sidereal
  let lagnaIdx = null;
  let lagnaPlacement = null;
  if (hasBirthTime) {
    const gst = Astronomy.SiderealTime(astroTime);
    const lstHours = gst + birthLocation.lng / 15;
    const lstDeg = mod360(lstHours * 15);
    const oblq = (23.4393 * Math.PI) / 180;
    const lat = (birthLocation.lat * Math.PI) / 180;
    const lst = (lstDeg * Math.PI) / 180;
    let asc = Math.atan2(
      Math.cos(lst),
      -(Math.sin(lst) * Math.cos(oblq) + Math.tan(lat) * Math.sin(oblq))
    );
    let tropAsc = mod360((asc * 180) / Math.PI);
    const diff = mod360(tropAsc - lstDeg);
    if (diff > 180) tropAsc = mod360(tropAsc + 180);
    const siderealAsc = mod360(tropAsc - ayanamsa);
    lagnaIdx = Math.floor(siderealAsc / 30);
    const lagnaSign = VEDIC_SIGNS[lagnaIdx];
    const lagnaDegreeFull = siderealAsc;
    const lagnaNk = getNakshatra(lagnaDegreeFull);
    lagnaPlacement = {
      sign: lagnaSign.name,
      signSanskrit: lagnaSign.sanskrit,
      signIndex: lagnaIdx,
      degree: +(siderealAsc % 30).toFixed(2),
      nakshatra: { name: lagnaNk.name, sanskrit: lagnaNk.sanskrit, pada: lagnaNk.pada, rulingPlanet: lagnaNk.rulingPlanet },
    };
  }

  // Build planet placements
  const planets = {};
  for (const key of Object.keys(siderealLong)) {
    const placement = makePlacement(siderealLong[key], lagnaIdx, hasBirthTime);
    const nk = getNakshatra(siderealLong[key]);
    planets[key] = {
      ...placement,
      nakshatra: { name: nk.name, sanskrit: nk.sanskrit, pada: nk.pada, rulingPlanet: nk.rulingPlanet },
    };
  }

  // Dasha (requires Moon nakshatra + birth date + today)
  const moonNk = getNakshatra(siderealLong.moon);
  const todayDate = new Date();
  let dasha = null;
  try {
    dasha = calcDasha(moonNk, utcDate, todayDate);
  } catch (e) {
    console.warn("Dasha calculation failed:", e);
  }

  return {
    ayanamsa: +ayanamsa.toFixed(4),
    lagna: lagnaPlacement,
    planets,
    dasha,
    hasBirthTime: !!hasBirthTime,
  };
}

